import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Moon, Sun, Monitor } from 'lucide-react';

export default function AppearanceSettings() {
  const { theme, toggleTheme } = useTheme();
  const [settings, setSettings] = useState({
    theme: 'system',
    fontSize: 'medium',
    reduceMotion: false,
    highContrast: false,
    compactMode: false
  });

  const themes = [
    { id: 'light', name: 'Light', icon: <Sun className="h-5 w-5" /> },
    { id: 'dark', name: 'Dark', icon: <Moon className="h-5 w-5" /> },
    { id: 'system', name: 'System', icon: <Monitor className="h-5 w-5" /> }
  ];

  const fontSizes = [
    { id: 'small', name: 'Small', scale: '0.875' },
    { id: 'medium', name: 'Medium', scale: '1' },
    { id: 'large', name: 'Large', scale: '1.125' },
    { id: 'xl', name: 'Extra Large', scale: '1.25' }
  ];

  return (
    <div className="space-y-8">
      {/* Theme Selection */}
      <div>
        <h3 className={`text-lg font-medium mb-4 ${
          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
        }`}>
          Theme
        </h3>
        <div className="grid grid-cols-3 gap-4">
          {themes.map((themeOption) => (
            <button
              key={themeOption.id}
              onClick={() => setSettings(prev => ({ ...prev, theme: themeOption.id }))}
              className={`
                flex items-center justify-center space-x-2 p-4 rounded-lg transition-colors
                ${settings.theme === themeOption.id
                  ? theme === 'dark'
                    ? 'bg-gray-700 ring-2 ring-blue-500'
                    : 'bg-white ring-2 ring-blue-500 shadow-sm'
                  : theme === 'dark'
                    ? 'bg-gray-800 hover:bg-gray-700'
                    : 'bg-gray-100 hover:bg-white hover:shadow-sm'
                }
              `}
            >
              {themeOption.icon}
              <span className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>
                {themeOption.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Font Size */}
      <div>
        <h3 className={`text-lg font-medium mb-4 ${
          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
        }`}>
          Font Size
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {fontSizes.map((size) => (
            <button
              key={size.id}
              onClick={() => setSettings(prev => ({ ...prev, fontSize: size.id }))}
              className={`
                p-4 rounded-lg transition-colors text-center
                ${settings.fontSize === size.id
                  ? theme === 'dark'
                    ? 'bg-gray-700 ring-2 ring-blue-500'
                    : 'bg-white ring-2 ring-blue-500 shadow-sm'
                  : theme === 'dark'
                    ? 'bg-gray-800 hover:bg-gray-700'
                    : 'bg-gray-100 hover:bg-white hover:shadow-sm'
                }
              `}
              style={{ fontSize: `${size.scale}rem` }}
            >
              {size.name}
            </button>
          ))}
        </div>
      </div>

      {/* Accessibility */}
      <div>
        <h3 className={`text-lg font-medium mb-4 ${
          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
        }`}>
          Accessibility
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Reduce Motion
              </p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Minimize animations and transitions
              </p>
            </div>
            <button
              onClick={() => setSettings(prev => ({ ...prev, reduceMotion: !prev.reduceMotion }))}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full
                transition-colors duration-200 ease-in-out focus:outline-none
                ${settings.reduceMotion
                  ? 'bg-blue-600'
                  : theme === 'dark'
                    ? 'bg-gray-700'
                    : 'bg-gray-200'
                }
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white shadow
                  transition-transform duration-200 ease-in-out
                  ${settings.reduceMotion ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                High Contrast
              </p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Increase contrast for better readability
              </p>
            </div>
            <button
              onClick={() => setSettings(prev => ({ ...prev, highContrast: !prev.highContrast }))}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full
                transition-colors duration-200 ease-in-out focus:outline-none
                ${settings.highContrast
                  ? 'bg-blue-600'
                  : theme === 'dark'
                    ? 'bg-gray-700'
                    : 'bg-gray-200'
                }
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white shadow
                  transition-transform duration-200 ease-in-out
                  ${settings.highContrast ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Layout */}
      <div>
        <h3 className={`text-lg font-medium mb-4 ${
          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
        }`}>
          Layout
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className={`font-medium ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Compact Mode
            </p>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Show more content with reduced spacing
            </p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, compactMode: !prev.compactMode }))}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full
              transition-colors duration-200 ease-in-out focus:outline-none
              ${settings.compactMode
                ? 'bg-blue-600'
                : theme === 'dark'
                  ? 'bg-gray-700'
                  : 'bg-gray-200'
              }
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow
                transition-transform duration-200 ease-in-out
                ${settings.compactMode ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end space-x-4">
        <button
          className={`
            px-4 py-2 rounded-lg transition-colors
            ${theme === 'dark'
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          `}
        >
          Reset to Default
        </button>
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}